import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Avatar, Spin, message, Upload } from 'antd';
import {
    RobotOutlined,
    SendOutlined,
    CloseOutlined,
    UserOutlined,
    MessageOutlined,
    ThunderboltOutlined,
    LoginOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    PaperClipOutlined,
    FileImageOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    WifiOutlined
} from '@ant-design/icons';
import { getImg } from '../../Api/getImg';
import popupSound from "../../assets/bubble-pop.mp3";
import DraggableChatIcon from './DraggableIcon';
const { TextArea } = Input;



// Simplified message formatting function
const formatMessageContent = (content) => {
    if (!content) return content;

    // Handle line breaks
    let formattedContent = content.replace(/\\n/g, '\n').replace(/\n/g, '<br/>');

    // Simple styling for semantic classes
    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*sectionHeader[^"]*"[^>]*)>/g, 
        '<$1 style="background: #e3edf7ff; border-left: 4px solid #298E9F; padding: 12px 16px; margin: 1px 0; border-radius: 6px; font-weight: 600;">'
    );

    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*listItem[^"]*"[^>]*)>/g, 
        '<$1 style=" border-left: 2px solid #298E9F; background: white; border-radius: 4px; padding:0px 5px ; display:flex; align-items:center ">'
    );

    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*content[^"]*"[^>]*)>/g, 
        '<$1 style=" color: #374151; margin: 8px 0;">'
    );

    formattedContent = formattedContent.replace(
        /<([^>]*class="[^"]*important[^"]*"[^>]*)>/g, 
        '<$1 style="background: #fef2f2; border: 1px solid #f87171; border-radius: 6px; padding: 12px; margin: 8px 0; color: #dc2626; font-weight: 500;">'
    );

    // Handle markdown elements
    formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formattedContent = formattedContent.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
    formattedContent = formattedContent.replace(/__(.*?)__/g, '<u>$1</u>');
    formattedContent = formattedContent.replace(/~~(.*?)~~/g, '<del>$1</del>');
    
    // Code blocks
    formattedContent = formattedContent.replace(/```(.*?)```/gs, '<pre style="background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin: 8px 0; overflow-x: auto; font-family: monospace;"><code>$1</code></pre>');
    formattedContent = formattedContent.replace(/`([^`]+)`/g, '<code style="background: #f1f5f9; padding: 2px 4px; border-radius: 3px; font-family: monospace;">$1</code>');

    // Headers
    formattedContent = formattedContent.replace(/^### (.*$)/gm, '<h3 style="font-size: 16px; font-weight: 600; margin: 12px 0 6px 0;">$1</h3>');
    formattedContent = formattedContent.replace(/^## (.*$)/gm, '<h2 style="font-size: 18px; font-weight: 700; margin: 16px 0 8px 0;">$1</h2>');
    formattedContent = formattedContent.replace(/^# (.*$)/gm, '<h1 style="font-size: 20px; font-weight: 800; margin: 20px 0 10px 0;">$1</h1>');

    // Lists
    formattedContent = formattedContent.replace(/^[\s]*[-*]\s(.*)$/gm, '<li style="margin: 4px 0; padding-left: 16px; position: relative;">$1</li>');
    formattedContent = formattedContent.replace(/^[\s]*\d+\.\s(.*)$/gm, '<li style="margin: 4px 0; padding-left: 16px;">$1</li>');

    // Links
    formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #298E9F; text-decoration: underline;" target="_blank">$1</a>');

    return formattedContent;
};

// Enhanced Message Component with image display and error handling
const EnhancedMessage = ({ message, isUser, user, onRetry }) => {
    const formattedContent = formatMessageContent(message.content);

    // Error message display
    if (message.isError) {
        return (
            <div className="space-y-2">
                <div className="p-3 rounded-lg text-sm break-words bg-red-50 border border-red-200 rounded-bl-sm">
                    <div className="flex items-start space-x-2">
                        <ExclamationCircleOutlined className="text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <div className="text-red-700 font-medium mb-1">
                                {message.errorType === 'network' ? 'Network Error' : 'Error'}
                            </div>
                            <div className="text-red-600 text-xs mb-2" dangerouslySetInnerHTML={{ __html: formattedContent }} />
                            {onRetry && message.canRetry && (
                                <Button
                                    size="small"
                                    type="primary"
                                    danger
                                    icon={<ReloadOutlined />}
                                    onClick={() => onRetry(message)}
                                    className="text-xs h-6"
                                >
                                    Retry
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-0">
            {/* Show attached file if exists */}
            {message.attachedFile && (
                <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs ${isUser ? 'bg-primary/10 border-primary/20' : 'bg-gray-100 border-gray-200'} border rounded-lg p-3`}>
                        {message.attachedFile.type?.startsWith('image/') ? (
                            <div className="space-y-2">
                                <img 
                                    src={message.attachedFile.preview} 
                                    alt={message.attachedFile.name}
                                    className="rounded max-w-full h-auto max-h-48 object-cover"
                                />
                                <p className="text-xs text-gray-600">{message.attachedFile.name}</p>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-2">
                                <FileImageOutlined className="text-gray-500" />
                                <span className="text-xs text-gray-600">{message.attachedFile.name}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Show API message image if exists */}
            {message.imgId && (
                <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-xs">
                        <img 
                            src={getImg(message.imgId)} 
                            alt="Message attachment"
                            className="rounded-lg max-w-full h-auto max-h-64 object-cover border shadow-sm"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                </div>
            )}
            
            {/* Message content */}
            <div
                className={`p-3 rounded-lg text-sm break-words ${isUser
                    ? 'bg-primary text-white rounded-br-sm'
                    : 'bg-white text-gray-800 rounded-bl-sm border border-gray-200'
                    }`}
                style={{ 
                    wordWrap: 'break-word', 
                    overflowWrap: 'break-word',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}
                dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
        </div>
    );
};

const AIAssistant = ({ isOpen, onToggle, user, slug, onLogin }) => {
    const [messages, setMessages] = useState([]);
    const [hasThread, setHasThread] = useState(false);
    const [threadCheckComplete, setThreadCheckComplete] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingThread, setIsCreatingThread] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [suggestedQuestions, setSuggestedQuestions] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        hasMore: true
    });

    const [chatPosition, setChatPosition] = useState({ x: 0, y: 0 });
    const shouldScrollRef = useRef(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
      const handleIconPositionChange = (newPosition) => {
        setChatPosition(newPosition);
    };
      const getChatWindowStyle = () => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const chatWidth = window.innerWidth >= 768 ? 384 : 340; // md:w-96 vs w-[340px]
        const chatHeight = 500;
        
        let left = chatPosition.x;
        let bottom = windowHeight - chatPosition.y + 80; // 80px offset from icon
        
        // Adjust if chat would go off screen
        if (left + chatWidth > windowWidth - 20) {
            left = chatPosition.x - chatWidth + 70; // Align to right edge of icon
        }
        
        if (bottom + chatHeight > windowHeight - 20) {
            bottom = 20;
        }

        return {
            position: 'fixed',
            left: `${Math.max(20, left)}px`,
            bottom: `${Math.max(20, bottom)}px`,
            zIndex: 50
        };
    };
    const isAuthorized =
        !!localStorage.getItem(`docTkn`) ||
        !!sessionStorage.getItem(`docTkn`);

    const getAuthToken = () => {
        return localStorage.getItem(`docTkn`) ||
            sessionStorage.getItem(`docTkn`);
    };

    // Network error detection helper
    const getNetworkErrorMessage = (error, response = null) => {
        if (!navigator.onLine) {
            return {
                type: 'network',
                message: 'No internet connection. Please check your network and try again.',
                canRetry: true
            };
        }

        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return {
                type: 'network',
                message: 'Unable to connect to the server. Please check your internet connection.',
                canRetry: true
            };
        }

        if (response) {
            switch (response.status) {
                case 408:
                case 502:
                case 503:
                case 504:
                    return {
                        type: 'network',
                        message: 'Server temporarily unavailable. Please try again in a moment.',
                        canRetry: true
                    };
                case 429:
                    return {
                        type: 'rate_limit',
                        message: 'Too many requests. Please wait a moment before trying again.',
                        canRetry: true
                    };
                case 401:
                    return {
                        type: 'auth',
                        message: 'Your session has expired. Please login again.',
                        canRetry: false
                    };
                case 413:
                    return {
                        type: 'file_size',
                        message: 'File is too large. Please select a smaller file.',
                        canRetry: false
                    };
                default:
                    return {
                        type: 'server',
                        message: `Server error (${response.status}). Please try again later.`,
                        canRetry: true
                    };
            }
        }

        return {
            type: 'unknown',
            message: 'An unexpected error occurred. Please try again.',
            canRetry: true
        };
    };

    // Add error message to chat
    const addErrorMessage = (errorInfo, originalMessage = null) => {
        const errorMessage = {
            id: Date.now() + Math.random(),
            type: 'ai',
            content: errorInfo.message,
            timestamp: new Date(),
            isError: true,
            errorType: errorInfo.type,
            canRetry: errorInfo.canRetry,
            originalMessage: originalMessage
        };

        setMessages(prev => [...prev, errorMessage]);
    };

    // Retry failed message
    const handleRetryMessage = async (errorMessage) => {
        if (!errorMessage.originalMessage) return;

        // Remove the error message
        setMessages(prev => prev.filter(msg => msg.id !== errorMessage.id));

        // Retry the original message
        const { content, file } = errorMessage.originalMessage;
        await sendMessageWithErrorHandling(content, file, true);
    };

    const scrollToBottom = () => {
        if (shouldScrollRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current && isAuthorized && hasThread) {
            inputRef.current.focus();
        }
    }, [isOpen, isAuthorized, hasThread]);

    useEffect(() => {
        if (isOpen && isAuthorized) {
            checkForExistingThread();
        }
    }, [isOpen, isAuthorized]);

    const audioRef = useRef(new Audio(popupSound));

    const playNotificationSound = () => {
        try {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(error => {
                console.log('Audio play failed:', error);
            });
        } catch (error) {
            console.log('Audio error:', error);
        }
    };

    // File handling functions
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (e.g., 10MB limit)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                message.error('File size must be less than 10MB');
                return;
            }

            // Validate file type (images, documents, etc.)
            const allowedTypes = [
                'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
                'application/pdf', 'text/plain', 'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ];
            
            if (!allowedTypes.includes(file.type)) {
                message.error('File type not supported. Please upload images, PDF, or document files.');
                return;
            }

            // Create preview URL for images
            let preview = null;
            if (file.type.startsWith('image/')) {
                preview = URL.createObjectURL(file);
            }

            setSelectedFile({
                file,
                name: file.name,
                type: file.type,
                size: file.size,
                preview
            });
        }
    };

    const removeSelectedFile = () => {
        if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
        }
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const checkForExistingThread = async () => {
        if (!isAuthorized) {
            setThreadCheckComplete(true);
            return;
        }

        try {
            setIsLoadingMessages(true);
            const token = getAuthToken();
            const response = await fetch(`https://api.dreasey.com/api/DrEasyAi/GetThreadMessages?PageNumber=1&PageSize=${pagination.pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                setHasThread(true);

                // Transform API messages to match our format
                const transformedMessages = (data.messages || []).map((msg, index) => ({
                    id: `${data.threadId}-${index}-${msg.createdAt}` || Date.now() + Math.random(),
                    type: msg.role === 'user' ? 'user' : 'ai',
                    content: msg.content || '',
                    timestamp: new Date(msg.createdAt),
                    role: msg.role,
                    imgId: msg.imgId // Add imgId from API response
                }));

                setMessages(transformedMessages.reverse());
                setPagination(prev => ({
                    ...prev,
                    pageNumber: data.pageNumber || 1,
                    hasMore: (data.totalMessages || 0) > (data.pageNumber || 1) * (data.pageSize || 10)
                }));
            } else if (response.status === 404) {
                setHasThread(false);
                setMessages([]);
            } else {
                const errorInfo = getNetworkErrorMessage(null, response);
                addErrorMessage(errorInfo);
                setHasThread(false);
            }
        } catch (error) {
            console.error('Error checking for thread:', error);
            const errorInfo = getNetworkErrorMessage(error);
            addErrorMessage(errorInfo);
            setHasThread(false);
        } finally {
            setIsLoadingMessages(false);
            setThreadCheckComplete(true);
        }
    };

    const createThread = async () => {
        try {
            setIsCreatingThread(true);
            const token = getAuthToken();
            
            const response = await fetch('https://api.dreasey.com/api/DrEasyAi/CreateThread', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                setHasThread(true);
                setMessages([]);
                setPagination({
                    pageNumber: 1,
                    pageSize: 20,
                    hasMore: false
                });
                message.success('Chat thread created successfully!');
            } else {
                const errorInfo = getNetworkErrorMessage(null, response);
                addErrorMessage(errorInfo);
                throw new Error(`Failed to create thread: ${response.status}`);
            }
        } catch (error) {
                            window.location.href = "/sub";

            console.error('Error creating thread:', error);
            const errorInfo = getNetworkErrorMessage(error);
            addErrorMessage(errorInfo);
        } finally {
            setIsCreatingThread(false);
        }
    };

    const loadMoreMessages = async () => {
        if (!pagination.hasMore || isLoadingMessages) return;

        try {
            setIsLoadingMessages(true);
            shouldScrollRef.current = false;

            const token = getAuthToken();
            const nextPage = pagination.pageNumber + 1;

            const response = await fetch(`https://api.dreasey.com/api/DrEasyAi/GetThreadMessages?PageNumber=${nextPage}&PageSize=${pagination.pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();

                const transformedMessages = (data.messages || []).map((msg, index) => ({
                    id: `${data.threadId}-${data.pageNumber}-${index}-${msg.createdAt}` || Date.now() + Math.random(),
                    type: msg.role === 'user' ? 'user' : 'ai',
                    content: msg.content || '',
                    timestamp: new Date(msg.createdAt),
                    role: msg.role,
                    imgId: msg.imgId // Add imgId from API response
                }));

                setMessages(prev => [...transformedMessages.reverse(), ...prev]);
                setPagination(prev => ({
                    ...prev,
                    pageNumber: nextPage,
                    hasMore: (data.totalMessages || 0) > nextPage * (data.pageSize || 10)
                }));
            } else {
                const errorInfo = getNetworkErrorMessage(null, response);
                addErrorMessage(errorInfo);
                throw new Error(`Failed to load more messages: ${response.status}`);
            }
        } catch (error) {
            console.error('Error loading more messages:', error);
            const errorInfo = getNetworkErrorMessage(error);
            addErrorMessage(errorInfo);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    // Enhanced send message with error handling
    const sendMessageWithErrorHandling = async (messageContent, fileToSend, isRetry = false) => {
        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: messageContent || (fileToSend ? `📎 ${fileToSend.name}` : ''),
            timestamp: new Date(),
            role: 'user',
            attachedFile: fileToSend
        };

        if (!isRetry) {
            setMessages(prev => [...prev, userMessage]);
        }

        setIsLoading(true);

        try {
            const token = getAuthToken();

            // Create FormData object as required by the API
            const formData = new FormData();
            formData.append('Message', messageContent || ''); 
            
            // Add file if selected
            if (fileToSend) {
                formData.append('File', fileToSend.file || fileToSend);
            }

            const response = await fetch('https://api.dreasey.com/api/DrEasyAi/SendMessage', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Don't set Content-Type header when using FormData
                },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                const aiResponse = {
                    id: Date.now() + 1,
                    type: 'ai',
                    content: data.responseMessage || 'Response received',
                    timestamp: new Date(),
                    imgId: data.imgId || null // Add imgId from response
                };
                setMessages(prev => [...prev, aiResponse]);
                playNotificationSound();
            } else {
                const errorInfo = getNetworkErrorMessage(null, response);
                addErrorMessage(errorInfo, {
                    content: messageContent,
                    file: fileToSend
                });

                // Remove the user message if sending failed and it's not a retry
                if (!isRetry) {
                    setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorInfo = getNetworkErrorMessage(error);
            addErrorMessage(errorInfo, {
                content: messageContent,
                file: fileToSend
            });

            // Remove the user message if sending failed and it's not a retry
            if (!isRetry) {
                setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
            }
        } finally {
            setIsLoading(false);
            // Clean up preview URL if it was created
            if (fileToSend?.preview) {
                URL.revokeObjectURL(fileToSend.preview);
            }
        }
    };

    const handleSendMessage = async () => {
        if ((!inputValue.trim() && !selectedFile) || !hasThread) return;

        if (!isAuthorized) {
            message.warning('Please login to continue using the AI assistant.');
            return;
        }
        
        shouldScrollRef.current = true;

        const messageToSend = inputValue.trim();
        const fileToSend = selectedFile;
        
        setInputValue('');
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        await sendMessageWithErrorHandling(messageToSend, fileToSend);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    useEffect(() => {
        if (isOpen && isAuthorized && hasThread) {
            fetchSuggestedQuestions();
        }
    }, [isOpen, isAuthorized, hasThread]);

    const fetchSuggestedQuestions = async () => {
        try {
            const token = getAuthToken();
            const response = await fetch("https://api.dreasey.com/api/DrEasyAi/GetSuggested", {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                const questions = data.map(q => ({ type: q.type, text: q.text }));
                setSuggestedQuestions(questions);
            } else {
                console.error('Failed to fetch suggested questions:', response.status);
            }
        } catch (error) {
            console.error("Error fetching suggested questions:", error);
        }
    };

    const handleSuggestedQuestion = async (questionObj) => {
        if (!isAuthorized || !hasThread) return;

        const { text, type } = questionObj;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: text,
            timestamp: new Date(),
            role: 'user'
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            const token = getAuthToken();
            
            const response = await fetch("https://api.dreasey.com/api/DrEasyAi/AnswerSuggested", {
                method: "POST",
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ questionType: type })
            });

            if (response.ok) {
                const data = await response.json();
                const aiResponse = {
                    id: Date.now() + 1,
                    type: 'ai',
                    content: data.answer || "No answer provided.",
                    timestamp: new Date(),
                    imgId: data.imgId || null // Add imgId from response
                };
                setMessages(prev => [...prev, aiResponse]);
                playNotificationSound();
            } else {
                const errorInfo = getNetworkErrorMessage(null, response);
                addErrorMessage(errorInfo, {
                    content: text,
                    type: 'suggested',
                    questionType: type
                });
            }
        } catch (error) {
            console.error("Error handling suggested question:", error);
            const errorInfo = getNetworkErrorMessage(error);
            addErrorMessage(errorInfo, {
                content: text,
                type: 'suggested',
                questionType: type
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = () => {
        if (onLogin) {
            onLogin();
        } else {
            window.location.href = `/${slug}/auth`;
        }
    };

    const SuggestedQuestions = () => {
        return (
            <div className="px-4 pb-2 border-t bg-white">
                {/* Add suggested questions UI here if needed */}
            </div>
        );
    };

  const renderWelcomeScreen = () => (
        <div className="flex-1 flex flex-col bg-white rounded-b-xl">
            <div className="flex-1 flex flex-col items-center rounded-b-xl justify-center p-6 bg-gradient-to-br from-blue-50/80 via-white to-purple-50/60 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-10 left-10 w-20 h-20 bg-blue-200/30 rounded-full animate-pulse"></div>
                    <div className="absolute top-20 right-16 w-12 h-12 bg-purple-200/40 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
                    <div className="absolute bottom-20 left-20 w-16 h-16 bg-green-200/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute bottom-32 right-8 w-8 h-8 bg-yellow-200/40 rounded-full animate-bounce" style={{ animationDelay: '1.5s' }}></div>
                </div>

                <div className="text-center space-y-2 relative z-10 max-w-sm mx-auto">
                    <div className="relative mx-auto w-24 h-24 group animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                        <div className="absolute inset-0 bg-gradient-to-r from-primary to-blue-600 rounded-full animate-ping opacity-20"></div>
                        <div className="absolute inset-2 bg-gradient-to-r from-primary/50 to-blue-600/50 rounded-full animate-pulse"></div>

                        <Avatar
                        src="https://res.cloudinary.com/dk2uh7sho/image/upload/v1754402435/WhatsApp_Image_2025-07-29_at_7.40.50_PM_hogkwx.jpg"
                            size={96}
                            className="bg-gradient-to-r from-primary to-blue-600 shadow-2xl border-4 border-white transform transition-all duration-300 hover:scale-110 hover:rotate-3 relative z-10"
                        />

                        <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-green-400 rounded-full border-4 border-white animate-pulse shadow-lg">
                            <div className="absolute inset-1 bg-green-300 rounded-full animate-ping"></div>
                        </div>

                        <div className="absolute -top-2 -left-2 w-2 h-2 bg-blue-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '0.3s' }}></div>
                        <div className="absolute -top-1 -right-3 w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce opacity-70" style={{ animationDelay: '0.7s' }}></div>
                        <div className="absolute -bottom-2 -left-3 w-1 h-1 bg-green-400 rounded-full animate-bounce opacity-50" style={{ animationDelay: '1.1s' }}></div>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-3 leading-tight">
                            Welcome to AI Medical Assistant
                        </h3>
                        <div className="h-1 w-16 bg-gradient-to-r from-primary to-blue-600 rounded-full mx-auto mb-4 animate-pulse"></div>
                        <p className="text-sm text-gray-600 leading-relaxed max-w-xs mx-auto">
                            Start your conversation with our intelligent medical assistant. Get instant help with
                            <span className="font-semibold text-primary"> appointments</span>,
                            <span className="font-semibold text-primary"> patient management</span>, and
                            <span className="font-semibold text-primary"> clinic operations</span>.
                        </p>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<PlayCircleOutlined className="animate-pulse" />}
                            onClick={createThread}
                            loading={isCreatingThread}
                            className="bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary border-0 shadow-2xl px-10 py-3 h-auto rounded-full font-semibold text-base transform transition-all duration-300 hover:scale-105 hover:shadow-primary/50 group"
                        >
                            <span className="flex items-center space-x-2">
                                <span>{isCreatingThread ? 'Creating Magic...' : 'Start Conversation'}</span>
                                {!isCreatingThread && <span className="text-lg">✨</span>}
                            </span>
                        </Button>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                        <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="font-medium">24/7 Available</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                                <span className="font-medium">Instant Responses</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                                <span className="font-medium">Medical Expertise</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600 group hover:text-primary transition-colors duration-200">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{ animationDelay: '0.9s' }}></div>
                                <span className="font-medium">Smart Assistance</span>
                            </div>
                        </div>
                    </div>

                    <div className="animate-fade-in-up" style={{ animationDelay: '1.4s' }}>
                        <p className="text-xs text-gray-400 mt-4 flex items-center justify-center space-x-1">
                            <span>✨</span>
                            <span>Powered by advanced AI technology</span>
                            <span>✨</span>
                        </p>
                    </div>
                </div>
            </div>

            <style jsx>{`
            @keyframes fade-in-up {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .animate-fade-in-up {
                opacity: 0;
                animation: fade-in-up 0.8s ease-out forwards;
            }

            @keyframes float {
                0%, 100% {
                    transform: translateY(0px);
                }
                50% {
                    transform: translateY(-10px);
                }
            }

            .animate-float {
                animation: float 3s ease-in-out infinite;
            }

            .scrollbar-hide::-webkit-scrollbar {
                display: none;
            }
        `}</style>
        </div>
    );
    const renderChatScreen = () => (
        <div className="flex-1 flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
                {pagination.hasMore && (
                    <div className="flex justify-center">
                        <Button
                            type="link"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={loadMoreMessages}
                            loading={isLoadingMessages}
                            className="text-gray-500 hover:text-primary"
                        >
                            {isLoadingMessages ? 'Loading...' : 'Load previous messages'}
                        </Button>
                    </div>
                )}

                {messages.map((message, index) => {
                    const isUser = message.type === 'user' || message.role === 'user';

                    return (
                        <div
                            key={message.id || index}
                            className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                        >
                            
                            <div className={`flex items-start space-x-2 max-w-[85%] ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                                {isUser ? (
                                    <Avatar
                                        src={user?.imageId ? getImg(user.imageId) : null}
                                        icon={!user?.imageId ? <UserOutlined /> : null}
                                        size={37}
                                        className="flex-shrink-0 shadow-sm"
                                    />
                                ) : (
                                   <div className="relative">
                                        <Avatar
                                            src="https://res.cloudinary.com/dk2uh7sho/image/upload/v1754402435/WhatsApp_Image_2025-07-29_at_7.40.50_PM_hogkwx.jpg"
                                            size={43}
                                            className={`flex-shrink-0 shadow-lg border-2 border-white mt-10 ${message.isError
                                                ? 'bg-red-500'
                                                : 'bg-gradient-to-r from-primary to-blue-600'
                                                }`}
                                        />
                                        <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${message.isError ? 'bg-red-400' : 'bg-green-400'
                                            }`}></div>
                                    </div>
                                )}

                                <div className="flex flex-col space-y-1 w-full">
                                    <EnhancedMessage 
                                        message={message} 
                                        isUser={isUser} 
                                        user={user} 
                                        onRetry={handleRetryMessage}
                                    />

                                    {message.timestamp && (
                                        <div className={`text-xs text-gray-400 ${isUser ? 'text-right' : 'text-left'}`}>
                                            {new Date(message.timestamp).toLocaleString([], {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="flex items-start space-x-2">
                            <Avatar
                            src="https://res.cloudinary.com/dk2uh7sho/image/upload/v1754402435/WhatsApp_Image_2025-07-29_at_7.40.50_PM_hogkwx.jpg"
                                size={45}
                                className="bg-gradient-to-r from-primary to-blue-600 flex-shrink-0 shadow-sm"
                            />
                            <div className="bg-white p-3 rounded-lg shadow-sm border">
                                <div className="flex space-x-1">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* <SuggestedQuestions /> */}

            {/* Network status indicator */}
            {!navigator.onLine && (
                <div className="px-4 py-2 bg-red-50 border-t border-red-200">
                    <div className="flex items-center space-x-2 text-red-600 text-sm">
                        <WifiOutlined className="text-red-500" />
                        <span>No internet connection</span>
                    </div>
                </div>
            )}

            {/* File preview section */}
            {selectedFile && (
                <div className="px-4 py-2 border-t bg-gray-50">
                    <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                        <div className="flex items-center space-x-3">
                            {selectedFile.type?.startsWith('image/') ? (
                                <img 
                                    src={selectedFile.preview} 
                                    alt={selectedFile.name}
                                    className="w-12 h-12 object-cover rounded border"
                                />
                            ) : (
                                <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center">
                                    <FileImageOutlined className="text-gray-500 text-lg" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                                <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                        </div>
                        <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={removeSelectedFile}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        />
                    </div>
                </div>
            )}

            {/* Input section */}
            <div className="p-4 border-t bg-white">
                <div className="flex space-x-2 items-end">
                    <div className="flex-1">
                        <TextArea
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message..."
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            className="border rounded-lg resize-none"
                            disabled={isLoading || !hasThread}
                        />
                    </div>
                    
                    {/* File upload button */}
                    <Button
                        type="default"
                        icon={<PaperClipOutlined />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading || !hasThread ||!navigator.onLine}
                        className="border-gray-300 hover:border-primary hover:text-primary"
                        title="Attach file"
                    />
                    
                    {/* Send button */}
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        disabled={(!inputValue.trim() ||!navigator.onLine) || isLoading || !hasThread}
                        className="bg-primary hover:bg-primary/80"
                        title="Send message"
                    />
                </div>
                
                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className="hidden"
                />
            </div>
        </div>
    );

    return (
        <>
             <DraggableChatIcon 
                isOpen={isOpen}
                onToggle={onToggle}
                onPositionChange={handleIconPositionChange}
            />
            {isOpen && (
                <div className="fixed bottom-16 right-6 md:right-28 z-50 w-[340px] md:w-96 h-[500px] bg-white rounded-2xl shadow-2xl border flex flex-col">
                    <div className="bg-gradient-to-r from-primary to-blue-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
                        <div className="relative">
                            <Avatar
                            src="https://res.cloudinary.com/dk2uh7sho/image/upload/v1754402435/WhatsApp_Image_2025-07-29_at_7.40.50_PM_hogkwx.jpg"
                                className="bg-white/20 backdrop-blur-sm border-2 border-white/30"
                                size={43}
                            />
                            <div className={`absolute -bottom-1 -right-1 w-3 h-3 ${navigator.onLine ? 'bg-green-400' : 'bg-red-400'} rounded-full border-2 border-white`}></div>
                        </div>
                        <div>
                            <h3 className="font-semibold text-sm flex items-center">
                                AI Medical Assistant
                                <ThunderboltOutlined className="ml-1 text-yellow-300 text-xs" />
                            </h3>
                            <p className="text-xs opacity-90">
                                {!navigator.onLine
                                    ? 'Offline'
                                    : !isAuthorized
                                        ? 'Login required'
                                        : !threadCheckComplete
                                            ? 'Loading...'
                                            : hasThread
                                                ? 'Always here to help'
                                                : 'Ready to start'}
                            </p>
                        </div>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={onToggle}
                            className="text-white hover:bg-blue-500"
                            size="small"
                        />
                    </div>

                    {!isAuthorized ? (
                        <div className="flex-1 flex flex-col items-center justify-center space-y-3 py-2">
                            <p className="text-sm text-gray-600 text-center">
                                Please login to chat with AI assistant
                            </p>
                            <Button
                                icon={<LoginOutlined />}
                                onClick={handleLogin}
                                className="border border-primary text-primary bg-white hover:bg-primary/10 hover:text-white"
                            >
                                Login to Continue
                            </Button>
                        </div>
                    ) : !threadCheckComplete ? (
                        <div className="flex-1 flex items-center justify-center">
                            <Spin size="large" />
                        </div>
                    ) : hasThread ? (
                        renderChatScreen()
                    ) : (
                        renderWelcomeScreen()
                    )}
                </div>
            )}
        </>
    );
};

export default AIAssistant;